import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { load } from "recaptcha-v3";

import {
  NotificationStatuses,
  trackEvent,
  reportError,
} from "@jsainsburyplc/id-ui-common";

import useCookies from "hooks/useCookies";
import { otpService } from "services/otp.service";
import { getResetPasswordLinkNoToken } from "utils/urls";
import { getBlackBoxToken } from "utils/iovation";
import { getErrorMessage } from "utils/otpForm";
import { actionTypes, categoryTypes } from "constants/tracking";
import { OTP_FORM_ID } from "constants/mfaAndOtp";
import {
  newCodeMessage,
  newCodeErrorMessage,
  MFA_CODE_SENT,
} from "constants/mfaAndOtp";
import { isRecapchaEnabled } from "utils/env";

import OtpForm from "components/OtpForm";
import PageFormat from "components/PageFormat";
import RecaptchaNotice from "components/RecaptchaNotice";
import useDigitalDataTracking from "hooks/useDigitalTrack";

const trackRequestCode = (action) => {
  trackEvent({
    category: categoryTypes.FORGOTTEN_PASSWORD_OTP,
    label: "Request new OTP code",
    action,
  });
};

const trackFormSubmit = () => {
  trackEvent({
    category: categoryTypes.FORGOTTEN_PASSWORD_OTP,
    action: actionTypes.SUBMIT,
    label: "Submit OTP code",
  });
};

const trackFormSubmitSuccess = () => {
  trackEvent({
    category: categoryTypes.FORGOTTEN_PASSWORD_OTP,
    action: actionTypes.SUCCESS,
    label: "Submit OTP code",
  });
};

const trackFormSubmitError = (code) => {
  trackEvent({
    category: categoryTypes.FORGOTTEN_PASSWORD_OTP,
    action: actionTypes.ERROR,
    label: `Submit OTP code: ${code}`,
  });
};

const getRecaptchaToken = async () => {
  if (isRecaptchaEnabledEnv) {
    try {
      const recaptcha = await load(process.env.REACT_APP_CAPTCHA_API_KEY_v3);
      const recaptchaToken = await recaptcha.execute("forgotpassword");
      return recaptchaToken;
    } catch (e) {
      reportError(
        `Forgotten password OTP - error occurred during getRecaptchaToken: ${e}`
      );
    }
  } else {
    return "";
  }
};

const isRecaptchaEnabledEnv = isRecapchaEnabled();

const OtpPage = () => {
  const history = useHistory();
  const { email } = useCookies();
  const [requestingNewCode, setRequestingNewCode] = useState(false);
  const [message, setMessage] = useState(undefined);

  useDigitalDataTracking(
    "web:groceries:authentication:forgotten password:verify email",
    "authentication",
    "authForgottenPasswordVerifyEmail",
    "view"
  );
  const submitHandler = async ({ code }, { setSubmitting }) => {
    setSubmitting(true);
    setMessage(undefined);
    try {
      trackFormSubmit();
      const ioBlackbox = getBlackBoxToken(OTP_FORM_ID);
      const response = await otpService.verifyOtp(code, email, ioBlackbox);
      if (response.ok) {
        const { forgotten_password_token: token } = await response.json();
        trackFormSubmitSuccess();
        history.push({
          pathname: getResetPasswordLinkNoToken(),
          state: { params: { token } },
        });
      } else {
        setSubmitting(false);
        const error = await response.json();
        throw error.code;
      }
    } catch (error) {
      trackFormSubmitError(error.code);
      setSubmitting(false);
      setMessage(getErrorMessage(error));
    }
  };

  const handleRequestNewCode = async (event) => {
    event && event.preventDefault();
    trackRequestCode(actionTypes.CODE_REQUESTED);
    setRequestingNewCode(true);

    try {
      const recaptchaToken = await getRecaptchaToken();
      const response = await otpService.requestForgotPasswordOtp({
        email,
        recaptchaToken,
      });
      if (response.ok) {
        trackRequestCode(actionTypes.SUCCESS);
        sessionStorage.setItem(MFA_CODE_SENT, "true");
        setRequestingNewCode(false);
        setMessage({
          type: NotificationStatuses.SUCCESS_TYPE,
          text: newCodeMessage,
        });
      } else {
        throw Error;
      }
    } catch (error) {
      trackRequestCode(actionTypes.ERROR);
      setRequestingNewCode(false);
      setMessage({
        type: NotificationStatuses.ERROR_TYPE,
        text: newCodeErrorMessage,
      });
    }
  };

  return (
    <PageFormat>
      <div className="c-hero c-hero--center">
        <h1>We've sent you a code</h1>
      </div>
      <p className="ln-u-margin-bottom*3">
        We just need to verify your details. We've sent a verification code to:
      </p>
      <p className="ln-u-margin-bottom*3 ln-u-font-weight-bold">{email}</p>
      <p>Please enter the code to reset your password:</p>

      <OtpForm
        onRequestCode={handleRequestNewCode}
        onSubmit={submitHandler}
        isRequestingCode={requestingNewCode}
        notification={message}
        isIovationEnabled={process.env.REACT_APP_IOVATION_ENABLED}
      />

      {isRecaptchaEnabledEnv && (
        <div className="ln-u-text-align-center">
          <RecaptchaNotice />
        </div>
      )}
    </PageFormat>
  );
};

export default OtpPage;
